import { MenuItem, styled, TextField, Typography } from '@mui/material';
import {
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { useCommonTranslation } from '../../hooks/useCommonTranslation';

export interface GroupedSelectCellMenuGroup {
  groupName: string;
  items: { label: string; value: string }[];
}

const StyledSelectedText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 14,
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'hasTopBorder',
})<{ hasTopBorder: boolean }>(({ theme, hasTopBorder }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTop: hasTopBorder ? `1px solid ${theme.palette.divider}` : 'none',
  borderColor: theme.palette.divider,
  fontSize: '18px',
}));

const GroupedSelectEditComponent = (props: GridRenderEditCellParams) => {
  const t = useCommonTranslation();
  const { id, value, field, colDef } = props;
  const options = (colDef as any).valueOptions as GroupedSelectCellMenuGroup[];
  const apiRef = useGridApiContext();

  const handleChange = (event: any) => {
    apiRef.current.setEditCellValue({
      id,
      field,
      value: event.target.value,
    });
  };

  return (
    <TextField
      select
      variant="outlined"
      value={value}
      onChange={handleChange}
      SelectProps={{
        displayEmpty: true,
        renderValue: (value) => {
          if (!value || value === '') {
            return (
              <StyledSelectedText>{t('common.select')}</StyledSelectedText>
            );
          }
          return (
            <StyledSelectedText>
              {(colDef as any).getOptionLabel(value)}
            </StyledSelectedText>
          );
        },
        MenuProps: {
          sx: {
            '& .MuiMenuItem-root.Mui-selected': {
              backgroundColor: 'white !important',
            },
            '& .MuiPaper-root': {
              width: 220,
            },
          },
        },
      }}>
      {options.map((group, groupIndex) => [
        group.groupName && (
          <StyledTypography
            key={`group-${groupIndex}`}
            hasTopBorder={groupIndex !== 0}>
            {group.groupName}
          </StyledTypography>
        ),
        group.items.map((item, itemIndex) => (
          <StyledMenuItem key={`${groupIndex}-${itemIndex}`} value={item.value}>
            {item.label}
          </StyledMenuItem>
        )),
      ])}
    </TextField>
  );
};

export const GroupedSelectEditCell = (params: any) => (
  <GroupedSelectEditComponent {...params} />
);
